import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface RMSETrendChartProps {
  loading?: boolean;
}

const RMSETrendChart: React.FC<RMSETrendChartProps> = ({ loading }) => {
  if (loading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 w-full h-full rounded-xl"></div>
      </div>
    );
  }

  const rmseData = useMemo(() => 
    Array.from({ length: 30 }, (_, i) => ({
      day: `Day ${i + 1}`,
      rmse: 3.5 - (i * 0.05) + Math.sin(i * 0.3) * 0.3,
      mae: 2.8 - (i * 0.04) + Math.sin(i * 0.2) * 0.2,
      r2: 0.85 + (i * 0.003) + Math.sin(i * 0.1) * 0.02
    })), []);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800/95 border border-slate-600/50 rounded-xl p-4 shadow-xl backdrop-blur-sm">
          <p className="text-slate-200 font-semibold mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-violet-400 font-bold">
              {entry.name}: {entry.value.toFixed(3)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-48 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rmseData}>
          <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" opacity={0.3} />
          <XAxis 
            dataKey="day" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#64748b' }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#64748b' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="rmse"
            stroke="#ef4444"
            strokeWidth={2}
            dot={false}
            name="RMSE"
          />
          <Line
            type="monotone"
            dataKey="mae"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={false}
            name="MAE"
          />
          <Line
            type="monotone"
            dataKey="r2"
            stroke="#10b981"
            strokeWidth={2}
            dot={false}
            name="R²"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RMSETrendChart;